import { useState } from 'react';
import { Instagram, Plus, X } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface InstagramCrosspostCardProps {
  enabled: boolean;
  mentions: string[];
  onEnabledChange: (enabled: boolean) => void;
  onMentionsChange: (mentions: string[]) => void;
  isSaving?: boolean;
  /** Si true, l'option AP est masquée (utilisateurs avec plan présence) */
  hideApOption?: boolean;
}

const AP_USERNAME = 'agencepersonnelle';
const MAX_MENTIONS = 3;

export function InstagramCrosspostCard({
  enabled,
  mentions,
  onEnabledChange,
  onMentionsChange,
  isSaving = false,
  hideApOption = false
}: Readonly<InstagramCrosspostCardProps>) {
  const [newMention, setNewMention] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Get custom mentions (exclude AP)
  const customMentions = mentions.filter(m => m !== AP_USERNAME);
  
  // Max custom mentions depends on AP being enabled (only if AP option is visible)
  const apIsActive = !hideApOption && enabled;
  const maxCustomMentions = apIsActive ? MAX_MENTIONS - 1 : MAX_MENTIONS;
  const canAddMore = customMentions.length < maxCustomMentions;

  // Validate username format (letters, numbers, underscores, dots)
  const isValidUsername = (username: string) => {
    const cleaned = username.replace(/^@/, '').trim();
    return /^[a-zA-Z0-9_.]{1,30}$/.test(cleaned);
  };

  const handleAddMention = () => {
    const cleaned = newMention.replace(/^@/, '').trim().toLowerCase();
    
    if (!cleaned) {
      setError('Entrez un nom d\'utilisateur');
      return;
    }

    if (!isValidUsername(cleaned)) {
      setError('Format invalide (lettres, chiffres, _ et . uniquement)');
      return;
    }

    if (cleaned === AP_USERNAME) {
      setError('Option réservée aux clients Émergence et Accélération');
      return;
    }

    if (mentions.includes(cleaned)) {
      setError('Cette mention existe déjà');
      return;
    }

    if (!canAddMore) {
      setError(`Maximum ${maxCustomMentions} mention(s) supplémentaire(s)`);
      return;
    }

    // Build new mentions array
    const newMentions = apIsActive 
      ? [AP_USERNAME, ...customMentions, cleaned]
      : [...customMentions, cleaned];
    
    onMentionsChange(newMentions);
    setNewMention('');
    setError(null);
  };

  const handleRemoveMention = (username: string) => {
    const filtered = mentions.filter(m => m !== username);
    onMentionsChange(filtered);
  };

  const handleEnabledChange = (newEnabled: boolean) => {
    onEnabledChange(newEnabled);
    
    // Update mentions array based on toggle
    if (newEnabled) {
      // Add AP if not present
      if (!mentions.includes(AP_USERNAME)) {
        onMentionsChange([AP_USERNAME, ...customMentions.slice(0, 1)]);
      }
    } else {
      // Remove AP
      onMentionsChange(customMentions);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddMention();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-gradient-to-br from-pink-500/20 to-purple-500/20">
          <Instagram className="h-5 w-5 text-pink-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-medium text-white">
            Partage sur Instagram
          </h3>
          <p className="text-sm text-white/60">
            Mentionner des comptes dans vos publications
          </p>
        </div>
      </div>
      
      {/* Content */}
      <div className="space-y-4">
        {/* AP Toggle - only show if user has access (non-presence plans) */}
        {!hideApOption && (
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-white">Partager sur</span>
              <Badge variant="secondary" className="bg-pink-500/10 text-pink-400 border-pink-500/20 w-fit">
                @{AP_USERNAME}
              </Badge>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={handleEnabledChange}
              disabled={isSaving}
            />
          </div>
        )}

        {/* Current mentions - filter out AP if hideApOption */}
        {mentions.some(m => hideApOption ? m !== AP_USERNAME : true) && (
          <div className="flex flex-wrap gap-2">
            {mentions
              .filter(m => hideApOption ? m !== AP_USERNAME : true)
              .map(mention => (
              <Badge 
                key={mention}
                variant="outline" 
                className={`
                  flex items-center gap-1 px-3 py-1.5
                  ${mention === AP_USERNAME 
                    ? 'bg-pink-500/10 text-pink-400 border-pink-500/30' 
                    : 'bg-white/5 text-white border-white/10'}
                `}
              >
                @{mention}
                {mention !== AP_USERNAME && (
                  <button
                    onClick={() => handleRemoveMention(mention)}
                    className="ml-1 hover:text-red-400 transition-colors"
                    disabled={isSaving}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
        )}

        {/* Add mention input */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">@</span>
              <Input
                value={newMention}
                onChange={(e) => {
                  setNewMention(e.target.value);
                  setError(null);
                }}
                onKeyDown={handleKeyDown}
                placeholder="username"
                className="pl-7 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                disabled={!canAddMore || isSaving}
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={handleAddMention}
              disabled={!canAddMore || !newMention.trim() || isSaving}
              className="border-white/10 text-white/60 hover:text-white hover:bg-white/10"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          
          {error && (
            <p className="text-xs text-red-400">{error}</p>
          )}
          
          <p className="text-xs text-white/40">
            {customMentions.length}/{maxCustomMentions} mention{maxCustomMentions > 1 ? 's' : ''} supplémentaire{maxCustomMentions > 1 ? 's' : ''}
            {!hideApOption && enabled && ' (+ @agencepersonnelle)'}
          </p>
        </div>
      </div>
    </div>
  );
}
